/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-06-26 11:39:10
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ButtonGroup, Button } from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { reduxForm, getFormSyncErrors, getFormValues } from 'redux-form';

import { RenderSelect2 } from 'src/components/views/common/render-select-2-v2';
import { FullscreenBar } from 'src/components/views/common/fullscreenBar';
import Settings from './settings';
import Preview from './preview';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { confirmLeavePage, buildOptionList } from 'src/helpers';

import history from 'src/routes/history';
import {
  buildInitialDesignSettingsValues,
  buildDesignSettingsValues
} from 'src/helpers/bundle-helper';

import { PLACEMENT_BUNDLE_OPTIONS } from 'src/constants/bundle';

import * as fontActions from 'actions/font-actions';
import * as bundleActions from 'actions/bundle-actions';
import * as filterSettingsActions from 'actions/filter-settings-actions';

import type {
  CreateRecommendationWidgetType,
  RecommendationWidgetType
} from 'src/models/recommendation';
import type { ComposedProps, State } from './index.type';

const BundleVisualEditor = (props: ComposedProps): Component<ComposedProps, State> => {
  const {
    i18n,
    getFonts,
    fontListSuccess,
    getFilterSettings,
    loadedFilterSettings,
    saving,
    saveSuccess,
    handleSubmit,
    touch,
    setStep,
    widget,
    currentStep,
    dirty,
    dirtyOnStep,
    isCreateFlow,
    createWidget,
    updateWidget,
    setDirtyOnStep,
    setWidget,
    formValues,
    syncError,
    handleCloseFullscreenPage
  } = props;
  const { placement } = widget;
  const [placementOptionList, setPlacementOptionList] = useState([]);
  const [selectedPlacement, setSelectedPlacement] = useState({});

  // Handle confirm leave page
  const handleConfirmLeavePage = event => {
    return confirmLeavePage(event, dirty, i18n);
  };

  useEffect(() => {
    if (!fontListSuccess) {
      getFonts();
    }

    if (!loadedFilterSettings && _.includes(placement, 'instant-search-widget')) {
      getFilterSettings();
    }

    const placementOptionList = buildOptionList(PLACEMENT_BUNDLE_OPTIONS, i18n);
    _.remove(placementOptionList, option => !_.includes(placement, option.value));
    setPlacementOptionList(placementOptionList);
    setSelectedPlacement(placementOptionList[0]);

    window.addEventListener('beforeunload', handleConfirmLeavePage);
    return () => {
      window.removeEventListener('beforeunload', handleConfirmLeavePage);
    };
  }, []);

  useEffect(() => {
    if (saveSuccess) {
      setStep(currentStep + 1);
    }
  }, [saving, saveSuccess]);

  // Handle action previous
  const handlePrevious = () => {
    const widgetSettings = buildDesignSettingsValues(widget, formValues);

    const _dirtyOnStep = _.clone(dirtyOnStep);
    _dirtyOnStep[`step_${currentStep}`] = dirty;

    setDirtyOnStep(_dirtyOnStep);
    setWidget(widgetSettings);
    setStep(currentStep - 1);
  };

  const disabledNextStep = () => {
    return !!Object.keys(syncError).length > 0;
  };

  // handle save settings
  const handleSave = data => {
    if (!Object.keys(syncError).length) {
      const widgetSettings = buildDesignSettingsValues(widget, data, true);
      if (isCreateFlow) {
        createWidget(widgetSettings);
      } else {
        updateWidget(widget.id, widgetSettings);
      }
    }
  };

  const isDisabledNextStep = disabledNextStep();

  const shouldConfirmWhenLeave =
    !_.isEmpty(_.filter(dirtyOnStep, (value, key) => key !== `step_${currentStep}` && value)) ||
    dirty;

  return (
    <div className="BC-Page PFS-PredictiveBundleItem">
      <FullscreenBar
        onAction={() => handleCloseFullscreenPage(!shouldConfirmWhenLeave)}
        // backTitle={i18n.translate('AdminHTML.Common.Action.exit')}
        title={i18n.translate('AdminHTML.PredictiveBundle.Item.step2Title')}
        middleContent={
          <div className="options-preview">
            <div className="options-preview-label">
              <span>{i18n.translate('AdminHTML.Common.Action.preview')}</span>
            </div>
            <div className="select-option-preview">
              <RenderSelect2
                name="placementPreview"
                component={RenderSelect2}
                options={placementOptionList}
                isDisabled={_.isEmpty(placementOptionList)}
                onChange={e => setSelectedPlacement(e)}
                value={selectedPlacement}
              />
            </div>
          </div>
        }
        actions={
          <ButtonGroup>
            <Button onClick={handlePrevious} disabled={saving}>
              {i18n.translate('AdminHTML.Common.Action.previous')}
            </Button>

            <Button
              primary
              onClick={handleSubmit(handleSave.bind(this))}
              disabled={isDisabledNextStep}
              loading={saving}>
              {i18n.translate('AdminHTML.Common.Action.saveAndNext')}
            </Button>
          </ButtonGroup>
        }
        progress={66.6}
      />
      <div className="BC-Widget-Editor-Page widget-visual-editor">
        <div className="visual-editor-body">
          <Settings widget={widget} touch={touch} />
          <Preview widget={widget} selectedPlacement={selectedPlacement} />
        </div>
      </div>
      {/* Confirm before leaving */}
      <RouteLeavingGuard
        when={shouldConfirmWhenLeave}
        // Navigate function
        navigate={path => history.push(path)}
        shouldBlockNavigation={shouldConfirmWhenLeave}
        isAvailableFullscreen={true}
      />
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  getFonts: () => dispatch(fontActions.getFonts(ownProps.i18n)),
  getFilterSettings: () =>
    dispatch(filterSettingsActions.getFilterSettings(undefined, ownProps.i18n)),
  createWidget: (widget: CreateRecommendationWidgetType) =>
    dispatch(bundleActions.createWidget(widget, ownProps.i18n)),
  updateWidget: (widgetId, widget: RecommendationWidgetType) =>
    dispatch(bundleActions.updateWidget(widgetId, widget, {}, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const { widget } = props;
  const { saving, saveSuccess } = state.bundle;
  const syncError = getFormSyncErrors('BundleVisualEditorForm')(state);
  const formValues = getFormValues('BundleVisualEditorForm')(state);
  const widgetDesignShareableSettings = _.get(state.bundle, 'widgetDesignShareableSettings', {});

  return {
    syncError,
    saving,
    saveSuccess,
    initialValues: buildInitialDesignSettingsValues(widget, widgetDesignShareableSettings || {}),
    formValues,
    fontListSuccess: _.get(state, 'font.fontListSuccess', false),
    loadedFilterSettings: _.get(state.filterSettings, 'success', false)
  };
};

const BundleVisualEditorForm = reduxForm({
  form: 'BundleVisualEditorForm', // a unique identifier for this form
  enableReinitialize: true
})(BundleVisualEditor);

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(BundleVisualEditorForm));
